import { useState } from "react";
// Eye and EyeOff icons not needed - using custom SVGs
import { Link, useNavigate } from "react-router-dom";
import { WorkspaceBrand } from "@/components/workspace/WorkspaceShell";
import "@/components/workspace/workspace.css";
import TermsAndConditions from "@/components/TermsConditions/TermsConditions";
import GoogleLoginButton from "@/components/GoogleLogin";
import ClassLinkLoginButton from "@/components/ClassLinkLogin";
import CleverLoginButton from "@/components/CleverLogin";
import { authClient } from "@/lib/auth-client";
import {
  useNotificationStore,
  NotificationType,
} from "@/store/useNotificationStore";

const SignUp = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isEmailTaken, setIsEmailTaken] = useState(false);
  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [isUsernameValid, setIsUsernameValid] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const { showNotification } = useNotificationStore();

  // Personal email domains to block
  const personalDomains = [
    "gmail.com",
    "yahoo.com",
    "hotmail.com",
    "outlook.com",
    "aol.com",
    "icloud.com",
    "mail.com",
    "protonmail.com",
  ];

  // Validate name field (checkEmpty = true for submit, false for blur)
  const validateName = (name: string, checkEmpty = true): string => {
    if (!name.trim()) {
      return checkEmpty ? "Please enter your name." : "";
    }
    if (name.trim().length < 2) {
      return "Please enter at least 2 characters.";
    }
    if (/\d/.test(name)) {
      return "Names shouldn't include numbers.";
    }
    if (/[#$%@!^&*()+=[\]{};:"\\|,.<>/?~`]/.test(name)) {
      return "Please use only letters, hyphens, or apostrophes.";
    }
    return "";
  };

  // Validate email field (checkEmpty = true for submit, false for blur)
  const validateEmail = (emailValue: string, checkEmpty = true): string => {
    if (!emailValue.trim()) {
      return checkEmpty ? "Please enter your email." : "";
    }
    // Check for valid email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRegex.test(emailValue)) {
      return "Please enter a valid email address.";
    }
    // Check for personal domains
    const domain = emailValue.split("@")[1]?.toLowerCase();
    if (domain && personalDomains.includes(domain)) {
      return "Please use your school or organization email.";
    }
    return "";
  };

  // Validate username (checkEmpty = true for submit, false for blur)
  const validateUsername = (
    usernameValue: string,
    checkEmpty = true,
  ): string => {
    if (!usernameValue.trim()) {
      return checkEmpty ? "Please enter a username." : "";
    }
    if (usernameValue.trim().length < 3) {
      return "Username must be at least 3 characters.";
    }
    return "";
  };

  // Check if username is taken (API call)
  const checkUsernameAvailability = async (usernameValue: string) => {
    if (!usernameValue.trim() || usernameValue.trim().length < 3) return;

    setIsCheckingUsername(true);
    setIsUsernameValid(false);
    try {
      const { data, error } = await authClient.isUsernameAvailable({
        username: usernameValue.trim(),
      });

      if (error) {
        setUsernameError("Unable to verify username availability.");
        return;
      }

      if (data?.available) {
        setIsUsernameValid(true);
      } else {
        setUsernameError("This username is already taken.");
      }
    } catch (err) {
      setUsernameError("Unable to verify username availability.");
    } finally {
      setIsCheckingUsername(false);
    }
  };

  // Check if email is already registered (API call)
  const checkEmailAvailability = async (
    emailValue: string,
  ): Promise<boolean> => {
    // BetterAuth does not expose a check-email endpoint here.
    // We treat the email as available and rely on signup error responses.
    return true;
  };

  // Validate password (checkEmpty = true for submit, false for blur)
  const validatePassword = (
    passwordValue: string,
    checkEmpty = true,
  ): string => {
    if (!passwordValue) {
      return checkEmpty ? "Please enter a password." : "";
    }
    if (passwordValue.length < 6) {
      return "Password must be at least 6 characters.";
    }
    return "";
  };

  // Validate confirm password (checkEmpty = true for submit, false for blur)
  const validateConfirmPassword = (
    confirmValue: string,
    checkEmpty = true,
  ): string => {
    if (!confirmValue) {
      return checkEmpty ? "Please confirm your password." : "";
    }
    if (confirmValue !== password) {
      return "Passwords don't match.";
    }
    return "";
  };

  const handleFirstNameChange = (value: string) => {
    setFirstName(value);
    if (firstNameError) {
      // Re-validate but only show non-empty errors while typing
      const error = validateName(value, false);
      setFirstNameError(error);
    }
  };

  const handleLastNameChange = (value: string) => {
    setLastName(value);
    if (lastNameError) {
      // Re-validate but only show non-empty errors while typing
      const error = validateName(value, false);
      setLastNameError(error);
    }
  };

  const handleFirstNameBlur = () => {
    // Only show invalid field errors on blur, not empty field errors
    setFirstNameError(validateName(firstName, false));
  };

  const handleLastNameBlur = () => {
    // Only show invalid field errors on blur, not empty field errors
    setLastNameError(validateName(lastName, false));
  };

  // Email change handler with domain fullness detection
  const handleEmailChange = (value: string) => {
    setEmail(value);
    // Clear error and valid state while typing
    if (emailError) {
      setEmailError("");
    }
    setIsEmailValid(false);
    setIsEmailTaken(false);
    // Domain fullness check - validate when we detect a complete domain pattern (skip empty check)
    if (value.includes("@") && /\.[a-z]{2,}$/i.test(value)) {
      // Show loader while "checking"
      setIsCheckingEmail(true);
      // Check format and domain first
      const error = validateEmail(value, false);
      if (error) {
        setEmailError(error);
        setIsCheckingEmail(false);
      } else {
        // If format is valid, check if email is already registered
        checkEmailAvailability(value).then((isAvailable) => {
          if (isAvailable) {
            setIsEmailValid(true);
          }
          setIsCheckingEmail(false);
        });
      }
    }
  };

  const handleEmailBlur = () => {
    // Only show invalid field errors on blur, not empty field errors
    if (!isCheckingEmail) {
      setEmailError(validateEmail(email, false));
    }
  };

  const handleUsernameChange = (value: string) => {
    setUsername(value);
    if (usernameError) {
      setUsernameError("");
    }
    setIsUsernameValid(false);
  };

  const handleUsernameBlur = () => {
    // Only show invalid field errors on blur, not empty field errors
    const error = validateUsername(username, false);
    setUsernameError(error);
    if (!error) {
      checkUsernameAvailability(username);
    }
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (passwordError) {
      // Re-validate but only show non-empty errors while typing
      setPasswordError(validatePassword(value, false));
    }
    // Also revalidate confirm password if it has a value
    if (confirmPassword && confirmPasswordError) {
      setConfirmPasswordError(
        value !== confirmPassword ? "Passwords don't match." : "",
      );
    }
  };

  const handlePasswordBlur = () => {
    // Only show invalid field errors on blur, not empty field errors
    setPasswordError(validatePassword(password, false));
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    if (confirmPasswordError) {
      // Re-validate but only show non-empty errors while typing
      setConfirmPasswordError(validateConfirmPassword(value, false));
    }
  };

  const handleConfirmPasswordBlur = () => {
    // Only show invalid field errors on blur, not empty field errors
    setConfirmPasswordError(validateConfirmPassword(confirmPassword, false));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validate all fields
    const firstNameValidation = validateName(firstName);
    const lastNameValidation = validateName(lastName);
    const emailValidation = validateEmail(email);
    const usernameValidation = validateUsername(username);
    const passwordValidation = validatePassword(password);
    const confirmPasswordValidation = validateConfirmPassword(confirmPassword);

    setFirstNameError(firstNameValidation);
    setLastNameError(lastNameValidation);
    setEmailError(emailValidation);
    setUsernameError(usernameValidation);
    setPasswordError(passwordValidation);
    setConfirmPasswordError(confirmPasswordValidation);

    if (
      firstNameValidation ||
      lastNameValidation ||
      emailValidation ||
      usernameValidation ||
      passwordValidation ||
      confirmPasswordValidation
    ) {
      return;
    }

    if (!isChecked) {
      showNotification(
        "Please agree to the terms and conditions",
        NotificationType.WARNING,
      );
      return;
    }

    setIsLoading(true);

    try {
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      const { data, error } = await authClient.signUp.email({
        username: username.trim(),
        password,
        email: email.trim(),
        name: fullName,
        callbackURL: "/",
        signupRole: "STUDENT",
      } as any);

      if (error || !data?.user) {
        throw new Error(
          error?.message || "Registration failed. Please try again.",
        );
      }

      // Account created successfully - redirect to login page
      // User must explicitly log in after registration
      showNotification(
        "Account created successfully! Please log in.",
        NotificationType.SUCCESS,
      );
      navigate("/login");
    } catch (err: any) {
      console.error("Registration error:", err);
      const errorMessage =
        (typeof err === "string" ? err : err?.message) ||
        "Registration failed. Please try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="et-auth">
      <div className="et-auth-form-side">
        <div className="et-auth-form">
          <div className="flex flex-col gap-2">
            {/* Left column in landscape: logo + heading */}
            <div>
              <Link to="/login" aria-label="Back to login">
                <WorkspaceBrand />
              </Link>
              <h1 className="text-darkgreen text-left">
                Hey amazing{" "}
                <span className="font-libre-baskerville italic">
                  Educators and Students!
                </span>
              </h1>
            </div>
            {/* Right column in landscape: OAuth + form */}
            <div className="et-signup-fields">
              <p className="mt-4 mobile-landscape:mt-0 text-sm font-bold text-center">
                Sign up with
              </p>
              <div className="et-auth-social">
                <GoogleLoginButton mode="signup" label="Google" />
                <CleverLoginButton mode="signup" label="Clever" />
                <ClassLinkLoginButton mode="signup" label="ClassLink" />
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">or</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <div className="flex flex-col lg:flex-row mobile-landscape:flex-col gap-[10px]">
                    <input
                      type="text"
                      aria-label="First name"
                      aria-invalid={!!firstNameError}
                      autoComplete="given-name"
                      value={firstName}
                      onChange={(e) => handleFirstNameChange(e.target.value)}
                      onBlur={handleFirstNameBlur}
                      className={`w-full h-[60px] bg-white border rounded-[10px] px-[15px] py-[16px] focus:outline-none focus:border-[#28543D9E] focus:shadow-[0_0_4px_0_#28543D4D] transition-all duration-200 font-mulish font-bold text-[20px] leading-none text-gray-900 placeholder-gray-500 placeholder:font-normal placeholder:text-[16px] ${
                        firstNameError ? "border-[#F87171]" : "border-[#E0E0E0]"
                      }`}
                      placeholder="Enter your first name"
                    />
                    <input
                      type="text"
                      aria-label="Last name"
                      aria-invalid={!!lastNameError}
                      autoComplete="family-name"
                      value={lastName}
                      onChange={(e) => handleLastNameChange(e.target.value)}
                      onBlur={handleLastNameBlur}
                      className={`w-full h-[60px] bg-white border rounded-[10px] px-[15px] py-[16px] focus:outline-none focus:border-[#28543D9E] focus:shadow-[0_0_4px_0_#28543D4D] transition-all duration-200 font-mulish font-bold text-[20px] leading-none text-gray-900 placeholder-gray-500 placeholder:font-normal placeholder:text-[16px] ${
                        lastNameError ? "border-[#F87171]" : "border-[#E0E0E0]"
                      }`}
                      placeholder="Enter your last name"
                    />
                  </div>
                  {(firstNameError || lastNameError) && (
                    <p
                      className="mt-2 font-mulish font-medium text-[15px]"
                      style={{ color: "#F87171" }}
                    >
                      {firstNameError || lastNameError}
                    </p>
                  )}
                </div>

                <div>
                  <div className="relative w-full">
                    <input
                      type="email"
                      aria-label="School email"
                      aria-invalid={!!emailError}
                      autoComplete="email"
                      value={email}
                      onChange={(e) => handleEmailChange(e.target.value)}
                      onBlur={handleEmailBlur}
                      className={`w-full h-[60px] bg-white border rounded-[10px] px-[15px] py-[16px] pr-[150px] focus:outline-none focus:border-[#28543D9E] focus:shadow-[0_0_4px_0_#28543D4D] transition-all duration-200 font-mulish font-bold text-[20px] leading-none text-gray-900 placeholder-gray-500 placeholder:font-normal placeholder:text-[16px] ${
                        emailError ? "border-[#F87171]" : "border-[#E0E0E0]"
                      }`}
                      placeholder="Enter your school email"
                    />
                    {isCheckingEmail && (
                      <div className="absolute inset-y-0 right-3 flex items-center">
                        <div className="flex items-center gap-[10px] w-[142px] h-[35px] bg-white border border-[#F5F5F5] rounded-[10px] p-[10px]">
                          <svg
                            className="animate-spin flex-shrink-0"
                            width="12"
                            height="12"
                            viewBox="0 0 14 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M7 14C3.143 14 0 10.857 0 7C0 5.691 0.364 4.417 1.05 3.311C1.253 2.982 1.687 2.884 2.016 3.087C2.345 3.29 2.443 3.72399 2.24 4.05299C1.694 4.93499 1.4 5.957 1.4 7C1.4 10.087 3.913 12.6 7 12.6C10.087 12.6 12.6 10.087 12.6 7C12.6 3.913 10.087 1.4 7 1.4C6.615 1.4 6.3 1.085 6.3 0.7C6.3 0.315 6.615 0 7 0C10.857 0 14 3.143 14 7C14 10.857 10.857 14 7 14Z"
                              fill="#666666"
                            />
                          </svg>
                          <span className="font-mulish text-xs text-gray-600">
                            Checking validity
                          </span>
                        </div>
                      </div>
                    )}
                    {isEmailValid && !isCheckingEmail && !emailError && (
                      <div className="absolute inset-y-0 right-3 flex items-center">
                        <div className="flex items-center gap-[10px] w-[108px] h-[35px] bg-[#E6FFE6] rounded-[10px] p-[10px]">
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M7 14C10.866 14 14 10.866 14 7C14 3.13401 10.866 0 7 0C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14Z"
                              fill="#57B856"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M4.81431 10.4139L2.25418 7.67377C1.90369 7.29864 1.92381 6.70513 2.29894 6.35463C2.67407 6.00414 3.26758 6.02431 3.61808 6.39939L5.59285 8.51295L8.71866 5.5924C8.74677 5.56611 8.77614 5.54203 8.80651 5.51987L10.2765 4.14649C10.6516 3.796 11.2452 3.81617 11.5956 4.1913C11.9461 4.56638 11.9259 5.15995 11.5508 5.51044L7.06125 9.70521L7.05646 9.70008L5.50345 11.1511L4.81431 10.4139Z"
                              fill="white"
                            />
                          </svg>
                          <span className="font-mulish font-bold text-[12px] leading-none text-[#28543D]">
                            Valid Email
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  {emailError && (
                    <p
                      className="mt-2 font-mulish font-medium text-[15px]"
                      style={{ color: "#F87171" }}
                    >
                      {emailError}
                    </p>
                  )}
                </div>

                <div>
                  <div className="relative w-full">
                    <input
                      type="text"
                      aria-label="Username"
                      aria-invalid={!!usernameError}
                      autoComplete="username"
                      value={username}
                      onChange={(e) => handleUsernameChange(e.target.value)}
                      onBlur={handleUsernameBlur}
                      className={`w-full h-[60px] bg-white border rounded-[10px] px-[15px] py-[16px] pr-[120px] focus:outline-none focus:border-[#28543D9E] focus:shadow-[0_0_4px_0_#28543D4D] transition-all duration-200 font-mulish font-bold text-[20px] leading-none text-gray-900 placeholder-gray-500 placeholder:font-normal placeholder:text-[16px] ${
                        usernameError ? "border-[#F87171]" : "border-[#E0E0E0]"
                      }`}
                      placeholder="Enter a username"
                    />
                    {isCheckingUsername && (
                      <div className="absolute inset-y-0 right-3 flex items-center">
                        <div className="flex items-center gap-[10px] h-[35px] bg-white border border-[#F5F5F5] rounded-[10px] p-[10px]">
                          <svg
                            className="animate-spin flex-shrink-0"
                            width="12"
                            height="12"
                            viewBox="0 0 14 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M7 14C3.143 14 0 10.857 0 7C0 5.691 0.364 4.417 1.05 3.311C1.253 2.982 1.687 2.884 2.016 3.087C2.345 3.29 2.443 3.72399 2.24 4.05299C1.694 4.93499 1.4 5.957 1.4 7C1.4 10.087 3.913 12.6 7 12.6C10.087 12.6 12.6 10.087 12.6 7C12.6 3.913 10.087 1.4 7 1.4C6.615 1.4 6.3 1.085 6.3 0.7C6.3 0.315 6.615 0 7 0C10.857 0 14 3.143 14 7C14 10.857 10.857 14 7 14Z"
                              fill="#666666"
                            />
                          </svg>
                          <span className="font-mulish text-xs text-gray-600">
                            Checking
                          </span>
                        </div>
                      </div>
                    )}
                    {isUsernameValid && !isCheckingUsername && (
                      <div className="absolute inset-y-0 right-3 flex items-center">
                        <div className="flex items-center gap-[10px] h-[35px] bg-[#E6FFE6] rounded-[10px] p-[10px]">
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M7 14C10.866 14 14 10.866 14 7C14 3.13401 10.866 0 7 0C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14Z"
                              fill="#57B856"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M4.81431 10.4139L2.25418 7.67377C1.90369 7.29864 1.92381 6.70513 2.29894 6.35463C2.67407 6.00414 3.26758 6.02431 3.61808 6.39939L5.59285 8.51295L8.71866 5.5924C8.74677 5.56611 8.77614 5.54203 8.80651 5.51987L10.2765 4.14649C10.6516 3.796 11.2452 3.81617 11.5956 4.1913C11.9461 4.56638 11.9259 5.15995 11.5508 5.51044L7.06125 9.70521L7.05646 9.70008L5.50345 11.1511L4.81431 10.4139Z"
                              fill="white"
                            />
                          </svg>
                          <span className="font-mulish font-bold text-[12px] leading-none text-[#28543D]">
                            Checked
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  {usernameError && (
                    <p
                      className="mt-2 font-mulish font-medium text-[15px]"
                      style={{ color: "#F87171" }}
                    >
                      {usernameError}
                    </p>
                  )}
                </div>

                <div>
                  <div className="relative w-full">
                    <input
                      type={showPassword ? "text" : "password"}
                      aria-label="Password"
                      aria-invalid={!!passwordError}
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => handlePasswordChange(e.target.value)}
                      onBlur={handlePasswordBlur}
                      className={`w-full h-[60px] bg-white border rounded-[10px] px-[15px] py-[16px] pr-12 focus:outline-none focus:border-[#28543D9E] focus:shadow-[0_0_4px_0_#28543D4D] transition-all duration-200 font-mulish font-bold text-[20px] leading-none text-gray-900 placeholder-gray-500 placeholder:font-normal placeholder:text-[16px] ${
                        passwordError ||
                        confirmPasswordError === "Passwords don't match."
                          ? "border-[#F87171]"
                          : "border-[#E0E0E0]"
                      }`}
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-[#28543D] hover:text-[#204833] transition-colors duration-200"
                    >
                      {showPassword ? (
                        <svg
                          width="20"
                          height="10"
                          viewBox="0 0 16 8"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M13.4621 1.81541C13.6862 1.6855 13.9732 1.76181 14.1032 1.98597L15.8935 5.07412C16.0234 5.29822 15.9471 5.58524 15.723 5.7152C15.4989 5.84511 15.2119 5.76881 15.0819 5.54464L13.2915 2.45649C13.1616 2.2324 13.2379 1.94538 13.4621 1.81541Z"
                            fill="#28543D"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M9.94917 3.47813C10.2044 3.43322 10.4476 3.60359 10.4926 3.85871L11.0476 7.00941C11.0925 7.26459 10.9222 7.50783 10.6671 7.5528C10.4119 7.59771 10.1686 7.42733 10.1237 7.17221L9.56858 4.02152C9.52368 3.7664 9.69405 3.5231 9.94917 3.47813Z"
                            fill="#28543D"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M6.00755 3.47139C6.26267 3.51624 6.43316 3.75941 6.38832 4.01459L5.83323 7.17311C5.78839 7.42823 5.54521 7.59872 5.29003 7.55388C5.03491 7.50904 4.86441 7.26586 4.90926 7.01068L5.46434 3.85216C5.50919 3.59704 5.75236 3.42655 6.00755 3.47139Z"
                            fill="#28543D"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M2.49389 1.81492C2.7183 1.94439 2.79529 2.23122 2.66584 2.45563L0.875497 5.55942C0.746048 5.78383 0.459192 5.86082 0.234782 5.73135C0.0103707 5.60194 -0.0666157 5.31505 0.0628333 5.09064L1.85318 1.98686C1.98262 1.76244 2.26948 1.68545 2.49389 1.81492Z"
                            fill="#28543D"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M0.182108 0.104184C0.383647 -0.0586205 0.678989 -0.0271605 0.841775 0.174359C2.1034 1.73635 4.39019 3.68237 7.98226 3.68237C11.5743 3.68237 13.861 1.73635 15.1227 0.174359C15.2855 -0.0271605 15.5808 -0.0586205 15.7823 0.104184C15.9839 0.266988 16.0153 0.562324 15.8525 0.763844C14.4873 2.45418 11.9581 4.62054 7.98226 4.62054C4.00634 4.62054 1.47719 2.45418 0.111933 0.763844C-0.0508522 0.562324 -0.0194297 0.266988 0.182108 0.104184Z"
                            fill="#28543D"
                          />
                        </svg>
                      ) : (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M6.2482 10.1701C4.79637 11.5422 3.85959 13.1933 3.45058 14.267C3.30312 14.6541 2.8698 14.8483 2.48272 14.7009C2.09564 14.5534 1.90139 14.1201 2.04885 13.733C2.52873 12.4733 3.58362 10.6245 5.21789 9.07992C6.86097 7.52706 9.12689 6.25 11.9997 6.25C14.8725 6.25 17.1385 7.52706 18.7815 9.07992C20.4158 10.6245 21.4707 12.4733 21.9506 13.733C22.098 14.1201 21.9038 14.5534 21.5167 14.7009C21.1296 14.8483 20.6963 14.6541 20.5488 14.267C20.1398 13.1933 19.2031 11.5422 17.7512 10.1701C16.3082 8.80628 14.3936 7.75 11.9997 7.75C9.60588 7.75 7.69124 8.80628 6.2482 10.1701ZM11.9997 11.75C10.7571 11.75 9.74971 12.7574 9.74971 14C9.74971 15.2426 10.7571 16.25 11.9997 16.25C13.2424 16.25 14.2497 15.2426 14.2497 14C14.2497 12.7574 13.2424 11.75 11.9997 11.75ZM8.24971 14C8.24971 11.9289 9.92865 10.25 11.9997 10.25C14.0708 10.25 15.7497 11.9289 15.7497 14C15.7497 16.0711 14.0708 17.75 11.9997 17.75C9.92865 17.75 8.24971 16.0711 8.24971 14Z"
                            fill="#28543D"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                  {passwordError && (
                    <p
                      className="mt-2 font-mulish font-medium text-[15px]"
                      style={{ color: "#F87171" }}
                    >
                      {passwordError}
                    </p>
                  )}
                </div>

                <div>
                  <div className="relative w-full">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      aria-label="Confirm password"
                      aria-invalid={!!confirmPasswordError}
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) =>
                        handleConfirmPasswordChange(e.target.value)
                      }
                      onBlur={handleConfirmPasswordBlur}
                      className={`w-full h-[60px] bg-white border rounded-[10px] px-[15px] py-[16px] pr-12 focus:outline-none focus:border-[#28543D9E] focus:shadow-[0_0_4px_0_#28543D4D] transition-all duration-200 font-mulish font-bold text-[20px] leading-none text-gray-900 placeholder-gray-500 placeholder:font-normal placeholder:text-[16px] ${
                        confirmPasswordError
                          ? "border-[#F87171]"
                          : "border-[#E0E0E0]"
                      }`}
                      placeholder="Re-enter your password"
                    />
                    <button
                      type="button"
                      aria-label={
                        showConfirmPassword
                          ? "Hide confirmation password"
                          : "Show confirmation password"
                      }
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-[#28543D] hover:text-[#204833] transition-colors duration-200"
                    >
                      {showConfirmPassword ? (
                        <svg
                          width="20"
                          height="10"
                          viewBox="0 0 16 8"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M13.4621 1.81541C13.6862 1.6855 13.9732 1.76181 14.1032 1.98597L15.8935 5.07412C16.0234 5.29822 15.9471 5.58524 15.723 5.7152C15.4989 5.84511 15.2119 5.76881 15.0819 5.54464L13.2915 2.45649C13.1616 2.2324 13.2379 1.94538 13.4621 1.81541Z"
                            fill="#28543D"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M9.94917 3.47813C10.2044 3.43322 10.4476 3.60359 10.4926 3.85871L11.0476 7.00941C11.0925 7.26459 10.9222 7.50783 10.6671 7.5528C10.4119 7.59771 10.1686 7.42733 10.1237 7.17221L9.56858 4.02152C9.52368 3.7664 9.69405 3.5231 9.94917 3.47813Z"
                            fill="#28543D"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M6.00755 3.47139C6.26267 3.51624 6.43316 3.75941 6.38832 4.01459L5.83323 7.17311C5.78839 7.42823 5.54521 7.59872 5.29003 7.55388C5.03491 7.50904 4.86441 7.26586 4.90926 7.01068L5.46434 3.85216C5.50919 3.59704 5.75236 3.42655 6.00755 3.47139Z"
                            fill="#28543D"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M2.49389 1.81492C2.7183 1.94439 2.79529 2.23122 2.66584 2.45563L0.875497 5.55942C0.746048 5.78383 0.459192 5.86082 0.234782 5.73135C0.0103707 5.60194 -0.0666157 5.31505 0.0628333 5.09064L1.85318 1.98686C1.98262 1.76244 2.26948 1.68545 2.49389 1.81492Z"
                            fill="#28543D"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M0.182108 0.104184C0.383647 -0.0586205 0.678989 -0.0271605 0.841775 0.174359C2.1034 1.73635 4.39019 3.68237 7.98226 3.68237C11.5743 3.68237 13.861 1.73635 15.1227 0.174359C15.2855 -0.0271605 15.5808 -0.0586205 15.7823 0.104184C15.9839 0.266988 16.0153 0.562324 15.8525 0.763844C14.4873 2.45418 11.9581 4.62054 7.98226 4.62054C4.00634 4.62054 1.47719 2.45418 0.111933 0.763844C-0.0508522 0.562324 -0.0194297 0.266988 0.182108 0.104184Z"
                            fill="#28543D"
                          />
                        </svg>
                      ) : (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M6.2482 10.1701C4.79637 11.5422 3.85959 13.1933 3.45058 14.267C3.30312 14.6541 2.8698 14.8483 2.48272 14.7009C2.09564 14.5534 1.90139 14.1201 2.04885 13.733C2.52873 12.4733 3.58362 10.6245 5.21789 9.07992C6.86097 7.52706 9.12689 6.25 11.9997 6.25C14.8725 6.25 17.1385 7.52706 18.7815 9.07992C20.4158 10.6245 21.4707 12.4733 21.9506 13.733C22.098 14.1201 21.9038 14.5534 21.5167 14.7009C21.1296 14.8483 20.6963 14.6541 20.5488 14.267C20.1398 13.1933 19.2031 11.5422 17.7512 10.1701C16.3082 8.80628 14.3936 7.75 11.9997 7.75C9.60588 7.75 7.69124 8.80628 6.2482 10.1701ZM11.9997 11.75C10.7571 11.75 9.74971 12.7574 9.74971 14C9.74971 15.2426 10.7571 16.25 11.9997 16.25C13.2424 16.25 14.2497 15.2426 14.2497 14C14.2497 12.7574 13.2424 11.75 11.9997 11.75ZM8.24971 14C8.24971 11.9289 9.92865 10.25 11.9997 10.25C14.0708 10.25 15.7497 11.9289 15.7497 14C15.7497 16.0711 14.0708 17.75 11.9997 17.75C9.92865 17.75 8.24971 16.0711 8.24971 14Z"
                            fill="#28543D"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                  {confirmPasswordError && (
                    <p
                      className="mt-2 font-mulish font-medium text-[15px]"
                      style={{ color: "#F87171" }}
                    >
                      {confirmPasswordError}
                    </p>
                  )}
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                    <p
                      className="font-mulish font-bold text-left text-[14px] leading-none"
                      style={{ color: "#FF6161" }}
                    >
                      {error}
                    </p>
                  </div>
                )}

                <TermsAndConditions
                  isChecked={isChecked}
                  setIsChecked={setIsChecked}
                />

                {isLoading ? (
                  <div className="flex items-center justify-center w-full bg-white border border-[#F5F5F5] rounded-[10px] px-5 py-3 gap-[10px]">
                    <svg
                      className="animate-spin flex-shrink-0"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7 14C3.143 14 0 10.857 0 7C0 5.691 0.364 4.417 1.05 3.311C1.253 2.982 1.687 2.884 2.016 3.087C2.345 3.29 2.443 3.72399 2.24 4.05299C1.694 4.93499 1.4 5.957 1.4 7C1.4 10.087 3.913 12.6 7 12.6C10.087 12.6 12.6 10.087 12.6 7C12.6 3.913 10.087 1.4 7 1.4C6.615 1.4 6.3 1.085 6.3 0.7C6.3 0.315 6.615 0 7 0C10.857 0 14 3.143 14 7C14 10.857 10.857 14 7 14Z"
                        fill="black"
                      />
                    </svg>
                    <span className="font-mulish font-bold text-[15px] leading-none text-gray-900">
                      Creating Account
                    </span>
                  </div>
                ) : (
                  <button
                    type="submit"
                    className="w-full bg-[#28543D] text-white hover:bg-[#204833] focus:bg-[#015C3A] font-bold rounded-xl transition-colors duration-200 flex items-center justify-center p-3 text-base"
                  >
                    Create Account
                  </button>
                )}
              </form>

              <div className="text-center mt-2">
                <p className="text-gray-600">
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    className="text-darkgreen hover:text-darkgreen/80 font-medium"
                  >
                    Login here
                  </Link>
                </p>
              </div>
            </div>
            {/* end right column */}
          </div>
        </div>
      </div>

      <aside className="et-auth-art">
        <img src="/design/learning-globe.webp" alt="" loading="lazy" />
        <p>
          A bold new future for
          <br />
          <em>1:1 learning.</em>
        </p>
      </aside>
    </div>
  );
};

export default SignUp;
